/**
 * Placement of a popover anchored to a table cell — pure, so the rule is
 * testable without a DOM (§101).
 *
 * Preferred: below the cell, left edge on the cell's left edge (the popover
 * extends to the RIGHT). When the viewport has less room to the right than
 * the popover needs, the popover opens to the LEFT: its right edge sits on
 * the cell's right edge. Same rule vertically: no room below → open above.
 * Room is measured against the VIEWPORT, not the table — the table scrolls
 * under a clipped container and its edges say nothing about visibility.
 * The popover is clamped inside the viewport in every case; the width it is
 * given is already capped by the caller (viewport − 2·margin).
 */

export interface Rect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface Placement {
  left: number;
  top: number;
  /** The popover's right edge is on the cell's right edge. */
  openLeft: boolean;
  /** The popover sits above the cell. */
  openUp: boolean;
}

export function placeAnchoredPopover(
  anchor: Rect,
  size: { width: number; height: number },
  viewport: { width: number; height: number },
  opts: { gap?: number; margin?: number } = {},
): Placement {
  const gap = opts.gap ?? 4;
  const margin = opts.margin ?? 8;
  const roomRight = viewport.width - margin - anchor.left;
  const openLeft = roomRight < size.width;
  let left = openLeft ? anchor.right - size.width : anchor.left;
  const roomBelow = viewport.height - margin - (anchor.bottom + gap);
  const openUp = roomBelow < size.height;
  let top = openUp ? anchor.top - gap - size.height : anchor.bottom + gap;
  // Clamp: a narrow window can leave the flipped side short too.
  left = Math.max(margin, Math.min(left, viewport.width - margin - size.width));
  top = Math.max(margin, Math.min(top, viewport.height - margin - size.height));
  return { left, top, openLeft, openUp };
}
